// frontend/src/components/reports/PartialPriceChip.jsx
//
// "This report's money columns are short by N items" — the one disclosure a
// money-valued report grid owes the merchant, and the one place it is worded.
//
// A report that multiplies quantities by a unit price can only price items the
// merchant has entered a price for. The backends skip the rest rather than
// pricing them at zero (core.services.cost_valuation.value_at_unit_price) and
// send the count of what they skipped. Blank cells alone do not explain
// themselves — a merchant reads them as "nothing here", not "we don't know" —
// so the count comes with the remedy, which is the same on every report: fix
// those prices in Data Health.
//
// TWO gaps, one component. A missing COST blanks value-at-cost, COGS and the
// inventory-cost columns; a missing SELL price blanks net sales, margin and
// GMROI. They have separate counts on the payload
// (`uncosted_excess_items` / `uncosted_items` vs `unpriced_items`), separate
// mutes, and separate Data Health cards — so `gap` picks the sentence, and a
// report short on both draws both chips side by side. Folding them into one
// would send a merchant to price costs for a sell-price problem.
//
// Renders nothing at zero, and nothing when the payload says not to warn:
// `partial_cost` / `partial_sell_price` is false for a merchant who muted that
// issue in Data health (backend cost_valuation.disclose_gap — the one home for
// that mute, shared with the inventory timeline's caveat). The numbers
// themselves are never muted; only this nag is.
//
// Sibling to the Home tiles' cascade in utils/kpiValueCoverage.js, which
// decides which SENTENCE a tile owes; this one is a single chip inside the
// report chrome.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '@mui/material';
import TabLink from '../../tabs/TabLink';
import { ICON, Warning } from '../../icons';

// One entry per gap: the sentence, and nothing else — both gaps are fixed on
// the same page, so the route is not a per-gap property.
const LABEL_KEY = {
  cost: 'reports.partialCost.chip',
  sellPrice: 'reports.partialSellPrice.chip',
};

/**
 * `gap` is REQUIRED and has no default. Defaulting it to `cost` would make the
 * one mistake this component exists to prevent — a sell-price chip whose author
 * forgot the prop rendering the cost sentence — silent and plausible: a chip
 * that reads correctly, counts correctly, and sends the merchant to fix the
 * wrong thing. An unknown gap raises rather than rendering an unlabelled chip.
 *
 * @param {'cost'|'sellPrice'} gap      which price is missing
 * @param {number|null|undefined} count items the report could not price
 * @param {boolean} [disclose]          the payload's `partial_cost` /
 *                                      `partial_sell_price`
 */
export default function PartialPriceChip({ gap, count, disclose = true }) {
  const { t } = useTranslation();
  if (!LABEL_KEY[gap]) {
    throw new Error(
      `PartialPriceChip: unknown gap ${JSON.stringify(gap)} — expected one of `
      + Object.keys(LABEL_KEY).join(', '));
  }
  if (!count || !disclose) return null;
  return (
    <Chip
      size="small"
      variant="outlined"
      color="warning"
      icon={<Warning size={ICON.sm} weight="fill" />}
      component={TabLink}
      to="/data-health"
      clickable
      label={t(LABEL_KEY[gap], { count })}
    />
  );
}
